import { useState, useEffect, useContext } from "react";

import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import ObjectList from "./ObjectList";
import { RoleContext } from "../context/RoleContext";
import DfnsService from "../services/DfnsService";
import { NomyxAction } from "../utils/Constants";

const ClaimTopicsPage = ({ service }) => {
  const navigate = useNavigate();
  const [claimTopics, setClaimTopics] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const { user, dfnsToken } = useContext(RoleContext);

  const columns = [
    { label: "Id", name: "attributes.topic" },
    { label: "Compliance Rule", name: "attributes.displayName", width: "95%" },
  ];

  const actions = [
    { label: "View", name: NomyxAction.ViewClaimTopic },
    { label: "Edit", name: NomyxAction.EditClaimTopic },
    // NOT FUNCTIONAL: removal does not reliably emit/propagate the on-chain event across
    // environments. See https://nomyx.atlassian.net/wiki/spaces/GEMI/pages/664141825/Event+Emission+Audit+and+Remediation+for+Claim+Topic+Removal+in+Multiple+Environments
    {
      label: "Remove",
      name: NomyxAction.RemoveClaimTopic,
      confirmation: "Are you sure you want to remove this Compliance Rule?",
    },
  ];

  const globalActions = [{ label: "Create Compliance Rule", name: NomyxAction.CreateClaimTopic }];

  const search = true;

  const getErrorMessage = (error) => {
    if (typeof error === "string") return error;
    if (error?.reason) return error.reason;
    if (error?.message) return error.message;
    if (error?.toString) return error.toString();
    return "An unknown error occurred";
  };

  const triggerRefresh = () => {
    setRefreshTrigger((prev) => !prev);
  };

  const checkClaimTopicRemoved = async (topic, maxAttempts = 10, intervalMs = 1000) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const topics = await service.getClaimTopics();
        const stillActive = topics?.some((t) => String(t.attributes.topic) === String(topic));

        if (!stillActive) {
          return true;
        }

        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
      } catch (error) {
        console.error(`Error checking compliance rule removal status (attempt ${attempt + 1}):`, error);
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
      }
    }

    return false;
  };

  const removeClaimTopic = async (record) => {
    const topic = record?.attributes?.topic;

    if (!topic) {
      toast.error("This compliance rule is missing its topic id and cannot be removed.");
      return;
    }

    try {
      await toast.promise(
        (async () => {
          const { initiateResponse, error: initError } = await DfnsService.initiateRemoveClaimTopic(Number(topic), user.walletId, dfnsToken);
          if (initError) throw new Error(initError);

          const { completeResponse, error: completeError } = await DfnsService.completeRemoveClaimTopic(
            user.walletId,
            dfnsToken,
            initiateResponse.challenge,
            initiateResponse.requestBody
          );
          if (completeError) throw new Error(completeError);

          const isRemoved = await checkClaimTopicRemoved(topic);

          if (!isRemoved) {
            throw new Error("Transaction completed but the compliance rule is still listed. Please refresh manually.");
          }

          triggerRefresh();

          return completeResponse;
        })(),
        {
          pending: "Removing Compliance Rule from blockchain and database...",
          success: "Compliance Rule has been successfully removed",
          error: {
            render({ data }) {
              const errorMessage = getErrorMessage(data);
              return <div>{errorMessage || "An error occurred while removing this Compliance Rule"}</div>;
            },
          },
        }
      );
    } catch (error) {
      console.error("Unexpected error during removeClaimTopic:", error);
      toast.error("We couldn't remove this compliance rule. Please try again.");
    }
  };

  const handleAction = async (event, action, record) => {
    switch (action) {
      case NomyxAction.CreateClaimTopic:
        navigate("/topics/create");
        break;
      case NomyxAction.ViewClaimTopic:
        navigate("/topics/" + record.id);
        break;
      case NomyxAction.EditClaimTopic:
        navigate("/topics/edit/" + record.id);
        break;
      case NomyxAction.RemoveClaimTopic:
        await removeClaimTopic(record);
        break;
      default:
        console.log("Unknown action: " + action);
    }
  };

  useEffect(() => {
    (async function () {
      const result = await service.getClaimTopics();
      setClaimTopics(result);
    })();
  }, [service, refreshTrigger]);

  return (
    <ObjectList
      title="Compliance Rules"
      description="Compliance Rules describe the types of Claims that can be created for any Identity"
      columns={columns}
      actions={actions}
      globalActions={globalActions}
      search={search}
      data={claimTopics}
      pageSize={10}
      onAction={handleAction}
      onGlobalAction={handleAction}
    />
  );
};

export default ClaimTopicsPage;
